// src/pages/admin/EnhancedAdminDashboard.tsx
"use client"
import React, { useState, useEffect } from "react"
import DashboardLayout from "../../components/layout/DashboardLayout"
import { useNavigate } from "react-router-dom"
import { 
  Users, 
  UserCheck, 
  Calendar, 
  Activity,
  BarChart3,
  PieChart,
  LineChart,
  Package as LucidePackage,
  FileText,
  
} from "lucide-react"
import {
  PieChart as RechartsPieChart,
  BarChart as RechartsBarChart,
  Pie,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import { 
  // fetchAllUsers, 
  // fetchUserCounts, 
  fetchCourseCounts, 
  fetchPackageCounts, 
  type CourseCountsResponse, 
  type PackageCountsResponse 
} from '../../api/packageService'
import '../../style/enhanced-admin-dashboard.css'

// ===================== PIE CHART COMPONENT =====================
const PieChartComponent = React.memo(({ data, label, onClick }: { data: { value: number, color: string }[], label: string, onClick?: () => void }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0)
  const formattedData = data.map((item) => ({
    ...item,
    name: label,
  }))

  return (
    <div className="chart-container" onClick={onClick}>
      <div style={{ width: '100%', height: '180px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <RechartsPieChart>
            <Pie
              data={formattedData}
              cx="50%"
              cy="50%"
              innerRadius={40}
              outerRadius={60}
              fill="#8884d8"
              dataKey="value"
              nameKey="name"
            >
              {formattedData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => [value, label]} />
            <Legend />
          </RechartsPieChart>
        </ResponsiveContainer>
      </div>
      <div className="chart-title">{label}</div>
      <div className="chart-total">Total: {total}</div>
    </div>
  );
})

// ===================== BAR CHART COMPONENT =====================
// const BarChartComponent = React.memo(({ data, label, dataKey, onClick }: { data: { name: string, [key: string]: any }[], label: string, dataKey: string, onClick?: () => void }) => {
//   return (
//     <div className="chart-container" onClick={onClick}>
//       <div style={{ width: '100%', height: '180px' }}>
//         <ResponsiveContainer width="100%" height="100%">
//           <RechartsBarChart
//             data={data}
//             margin={{
//               top: 5,
//               right: 30,
//               left: 20,
//               bottom: 40,
//             }}
//           >
//             <CartesianGrid strokeDasharray="3 3" />
//             <XAxis 
//               dataKey="name" 
//               angle={-45} 
//               textAnchor="end" 
//               height={40}
//               fontSize={12}
//             />
//             <YAxis fontSize={12} />
//             <Tooltip />
//             <Legend />
//             <Bar dataKey={dataKey} fill="#8884d8" name={label} />
//           </RechartsBarChart>
//         </ResponsiveContainer>
//       </div>
//       <div className="chart-title">{label}</div>
//     </div>
//   );
//})

// ===================== PACKAGE STATUS CHART =====================
const PackageStatusChart = React.memo(({ packageCounts, onClick }: { packageCounts: PackageCountsResponse, onClick?: () => void }) => {
  const published = packageCounts.statusCounts.find(s => s.status === 'Published')?.count || 0
  const draft = packageCounts.statusCounts.find(s => s.status === 'Draft')?.count || 0
  const soldOut = packageCounts.statusCounts.find(s => s.status === 'Sold out')?.count || 0

  const data = [
    { value: published, color: '#10B981', label: `Published (${published})` },
    { value: draft, color: '#F59E0B', label: `Draft (${draft})` },
    { value: soldOut, color: '#EF4444', label: `Sold Out (${soldOut})` },
  ]

  return (
    <div className="chart-container" onClick={onClick}>
      <div style={{ width: '100%', height: '180px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <RechartsPieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={40}
              outerRadius={60}
              fill="#8884d8"
              dataKey="value"
              nameKey="label"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value, name) => [value, name]} />
            <Legend />
          </RechartsPieChart>
        </ResponsiveContainer>
      </div>
      <div className="chart-title">Package Status</div>
    </div>
  );
})

// ===================== COURSE STATUS CHART =====================
const CourseStatusChart = React.memo(({ courseCounts, onClick }: { courseCounts: CourseCountsResponse, onClick?: () => void }) => {
  const published = courseCounts.statusCounts.find(s => s.status === 'Published')?.count || 0
  const draft = courseCounts.statusCounts.find(s => s.status === 'Draft')?.count || 0
  const soldOut = courseCounts.statusCounts.find(s => s.status === 'Sold out')?.count || 0

  const data = [
    { value: published, color: '#10B981', label: `Published (${published})` },
    { value: draft, color: '#F59E0B', label: `Draft (${draft})` },
    { value: soldOut, color: '#EF4444', label: `Sold Out (${soldOut})` },
  ]

  return (
    <div className="chart-container" onClick={onClick}>
      <div style={{ width: '100%', height: '180px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <RechartsPieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={40}
              outerRadius={60}
              fill="#8884d8"
              dataKey="value"
              nameKey="label"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value, name) => [value, name]} />
            <Legend />
          </RechartsPieChart>
        </ResponsiveContainer>
      </div>
      <div className="chart-title">Course Status</div>
    </div>
  );
})

// ===================== USER STATUS CHART =====================
const UserStatusChart = React.memo(({ active, inactive, onClick }: { active: number, inactive: number, onClick?: () => void }) => {
  const data = [
    { value: active, color: '#10B981', label: `Active (${active})` },
    { value: inactive, color: '#EF4444', label: `Inactive (${inactive})` }
  ]

  return (
    <div className="chart-container" onClick={onClick}>
      <div style={{ width: '100%', height: '180px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <RechartsPieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={40}
              outerRadius={60}
              fill="#8884d8"
              dataKey="value"
              nameKey="label"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value, name) => [value, name]} />
            <Legend />
          </RechartsPieChart>
        </ResponsiveContainer>
      </div>
      <div className="chart-title">User Status</div>
    </div>
  );
})

// ===================== USER ACTIVITY BAR CHART =====================
const UserActivityBarChart = React.memo(({ totalUsers, activeUsers, inactiveUsers, onClick }: { totalUsers: number, activeUsers: number, inactiveUsers: number, onClick?: () => void }) => {
  const data = [
    { name: 'Total Users', value: totalUsers },
    { name: 'Active Users', value: activeUsers },
    { name: 'Inactive Users', value: inactiveUsers }
  ];

  return (
    <div className="chart-container" onClick={onClick}>
      <div style={{ width: '100%', height: '180px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <RechartsBarChart
            data={data}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 40,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="name" 
              angle={-45} 
              textAnchor="end" 
              height={40}
              fontSize={12}
            />
            <YAxis fontSize={12} />
            <Tooltip />
            <Legend />
            <Bar dataKey="value" fill="#8884d8" name="Users" />
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
      <div className="chart-title">User Activity Overview</div>
    </div>
  );
})

// ===================== MAIN DASHBOARD =====================
const EnhancedAdminDashboard: React.FC = () => {
  const navigate = useNavigate()
  const [totalUsers, setTotalUsers] = useState(0)
  const [activeUsers, setActiveUsers] = useState(0)
  const [inactiveUsers, setInactiveUsers] = useState(0)
  const [courseCounts, setCourseCounts] = useState<CourseCountsResponse>({ totalCount: 0, statusCounts: [] })
  const [packageCounts, setPackageCounts] = useState<PackageCountsResponse>({ totalCount: 0, statusCounts: [] })
  const [currentTime, setCurrentTime] = useState(new Date())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        const [
          // usersRes, userCountsRes,
           coursesRes, packagesRes] = await Promise.all([
          // fetchAllUsers(),
          // fetchUserCounts(),
          fetchCourseCounts(),
          fetchPackageCounts()
        ])

        // setTotalUsers(userCountsRes.totalCount || usersRes.users.length)
        // setActiveUsers(userCountsRes.activeCount || 0)
        // setInactiveUsers(userCountsRes.deletedCount || 0)
        setCourseCounts(coursesRes)
        setPackageCounts(packagesRes)
      } catch (err) {
        console.error(err)
        setError("Failed to load dashboard data.")
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [])

  const handleNavigate = (path: string) => navigate(path)

  if (loading) {
    return (
      <DashboardLayout title="Admin Dashboard">
        <div className="loading-container"><p>Loading dashboard data...</p></div>
      </DashboardLayout>
    )
  }

  if (error) {
    return (
      <DashboardLayout title="Admin Dashboard">
        <div className="error-container">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout title="Admin Dashboard">
      <div className="enhanced-admin-dashboard">
        {/* HEADER */}
        <div className="dashboard-header">
          <div className="header-content">
            <h1 className="dashboard-title">Welcome Back, Admin!</h1>
            <p className="dashboard-subtitle">Here's your platform overview</p>
          </div>
          <div className="header-info">
            <div className="time-display">
              <Calendar className="time-icon" />
              <span>{currentTime.toLocaleDateString()}</span>
            </div>
            <div className="time-display">
              <Activity className="time-icon" />
              <span>{currentTime.toLocaleTimeString()}</span>
            </div>
          </div>
        </div>

        {/* STATS CARDS */}
        {/* <div className="stats-grid">
          {[
            { title: "Total Users", value: totalUsers, icon: Users, color: "bg-blue-500", path: "/user-list" },
            { title: "Active Users", value: activeUsers, icon: UserCheck, color: "bg-green-500", path: "/user-list?filter=active" },
          ].map((card, i) => (
            <div key={i} className="stat-card" onClick={() => handleNavigate(card.path)}>
              <div className={`stat-icon ${card.color}`}>
                <card.icon size={16} className="text-white" />
              </div>
              <div className="stat-content">
                <h3 className="stat-title">{card.title}</h3>
                <p className="stat-value">{card.value.toLocaleString()}</p>
              </div>
            </div>
          ))}
        </div> */}

        {/* PACKAGE & COURSE SECTIONS */}
        <div className="management-grid">
          {/* PACKAGE SECTION */}
          <div className="user-management-section">
            <div className="section-header">
              <h3 className="section-title">Package Management</h3>
              <button className="view-all-btn" onClick={() => handleNavigate("/package")}>View All</button>
            </div>
            <div className="user-management-cards">
              <div className="management-card" onClick={() => handleNavigate('/package')}>
                <div className="management-card-icon bg-blue-100 text-blue-600">
                  <LucidePackage size={16} />
                </div>
                <div className="management-card-content">
                  <h4>Total Packages</h4>
                  <p>{packageCounts.totalCount}</p>
                </div>
              </div>
            </div>
          </div>

          {/* COURSE SECTION */}
          <div className="user-management-section">
            <div className="section-header">
              <h3 className="section-title">Course Management</h3>
              <button className="view-all-btn" onClick={() => handleNavigate("/courses")}>View All</button>
            </div>
            <div className="user-management-cards">
              <div className="management-card" onClick={() => handleNavigate('/courses')}>
                <div className="management-card-icon bg-blue-100 text-blue-600">
                  <FileText size={16} />
                </div>
                <div className="management-card-content">
                  <h4>Total Courses</h4>
                  <p>{courseCounts.totalCount}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CHARTS */}
        <div className="charts-section">
          {/* USER CHARTS ROW */}
          {/* <div className="chart-row">
            <div className="chart-card">
              <div className="chart-header">
                <h3>User Statistics</h3>
                <BarChart3 className="chart-icon" size={16} />
              </div>
              <div className="doughnut-grid">
                <PieChartComponent data={[{ value: totalUsers, color: '#3B82F6' }]} label="Total Users" onClick={() => handleNavigate('/user-list')} />
                <UserStatusChart active={activeUsers} inactive={inactiveUsers} onClick={() => handleNavigate('/user-list')} />
                <UserActivityBarChart 
                  totalUsers={totalUsers} 
                  activeUsers={activeUsers} 
                  inactiveUsers={inactiveUsers} 
                  onClick={() => handleNavigate('/user-list')} 
                />
              </div>
            </div>
          </div> */}

          {/* PACKAGE CHARTS ROW */}
          <div className="chart-row">
            <div className="chart-card">
              <div className="chart-header">
                <h3>Package Statistics</h3>
                <PieChart className="chart-icon" size={16} />
              </div>
              <div className="doughnut-grid">
                <PieChartComponent data={[{ value: packageCounts.totalCount, color: '#3B82F6' }]} label="Total Packages" onClick={() => handleNavigate('/package')} />
                <PackageStatusChart packageCounts={packageCounts} onClick={() => handleNavigate('/package')} />
              </div>
            </div>
          </div>

          {/* COURSE CHARTS ROW */}
          <div className="chart-row">
            <div className="chart-card">
              <div className="chart-header">
                <h3>Course Statistics</h3>
                <LineChart className="chart-icon" size={16} />
              </div>
              <div className="doughnut-grid">
                <PieChartComponent data={[{ value: courseCounts.totalCount, color: '#8B5CF6' }]} label="Total Courses" onClick={() => handleNavigate('/courses')} />
                <CourseStatusChart courseCounts={courseCounts} onClick={() => handleNavigate('/courses')} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}

export default EnhancedAdminDashboard